'use client';

/**
 * AchievementToast — slides in from the top-right when an achievement unlocks.
 *
 * Style: cream paper + ink border + 4px gold accent stripe on the left.
 * Pixel-text typography, no neon, no soft shadows. Plays the level-up sting
 * on appear. Click-to-dismiss; auto-dismisses after 4 seconds.
 *
 * Parents own the queue: pass `achievement={...}` to show one, then handle
 * `onDismiss` to advance to the next.
 */

import { useEffect, useState } from 'react';
import { playSfx } from '@/engine/audio';
import { PixelText } from '@/components/ui/pixel-text';
import type { Achievement } from '@/engine/achievements';

type Props = {
  achievement: Achievement | null;
  onDismiss: () => void;
};

const VISIBLE_MS = 4000;
const SLIDE_MS = 250;

export function AchievementToast({ achievement, onDismiss }: Props) {
  // 'enter' immediately after mount kicks the slide-in; 'exit' kicks the
  // slide-out; null means we shouldn't render the panel at all.
  const [phase, setPhase] = useState<'enter' | 'visible' | 'exit' | null>(null);

  useEffect(() => {
    if (!achievement) {
      setPhase(null);
      return;
    }

    // Initial mount: start off-screen, then flip to visible on the next frame
    // so CSS transitions animate. Play the sting once.
    setPhase('enter');
    playSfx('level_up');

    const raf = requestAnimationFrame(() => setPhase('visible'));

    const visibleTimer = window.setTimeout(() => {
      setPhase('exit');
    }, VISIBLE_MS);

    const exitTimer = window.setTimeout(() => {
      onDismiss();
    }, VISIBLE_MS + SLIDE_MS);

    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(visibleTimer);
      window.clearTimeout(exitTimer);
    };
  }, [achievement, onDismiss]);

  if (!achievement || phase === null) return null;

  const offscreen = phase === 'enter' || phase === 'exit';

  const handleClick = () => {
    setPhase('exit');
    window.setTimeout(onDismiss, SLIDE_MS);
  };

  return (
    <div
      role="status"
      aria-live="polite"
      onClick={handleClick}
      style={{
        position: 'fixed',
        top: 16,
        right: 16,
        width: 320,
        zIndex: 9999,
        cursor: 'pointer',
        transform: offscreen ? 'translateX(120%)' : 'translateX(0)',
        transition: `transform ${SLIDE_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`,
        display: 'flex',
        backgroundColor: '#f5ecd6', // cream paper
        border: '2px solid #1a1a1a', // ink border
        boxShadow: '2px 2px 0 #1a1a1a',
        imageRendering: 'pixelated',
        fontFamily: 'monospace',
      }}
    >
      {/* Gold accent stripe */}
      <div
        aria-hidden
        style={{
          width: 4,
          backgroundColor: '#c5a83a', // gold
          flexShrink: 0,
        }}
      />

      {/* Icon column */}
      <div
        aria-hidden
        style={{
          flexShrink: 0,
          padding: '12px 8px 12px 12px',
          fontSize: 32,
          lineHeight: 1,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {achievement.icon}
      </div>

      {/* Text column */}
      <div
        style={{
          flex: 1,
          padding: '10px 12px 12px 4px',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          minWidth: 0,
        }}
      >
        <PixelText
          as="span"
          size="sm"
          glow="none"
          style={{
            color: '#5c4a30', // walnut — small caps label
            fontSize: 10,
            letterSpacing: '0.15em',
          }}
        >
          Achievement Unlocked
        </PixelText>
        <PixelText
          as="span"
          size="md"
          glow="none"
          style={{
            color: '#1a1a1a', // ink
            fontWeight: 700,
            fontSize: 14,
            letterSpacing: '0.05em',
          }}
        >
          {achievement.name}
        </PixelText>
        <PixelText
          as="span"
          size="sm"
          glow="none"
          style={{
            color: '#1a1a1a', // ink
            fontSize: 11,
            textTransform: 'none',
            letterSpacing: '0.02em',
            fontWeight: 400,
            lineHeight: 1.35,
          }}
        >
          {achievement.description}
        </PixelText>
      </div>
    </div>
  );
}
